import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { PlusPromo } from "../../components/PlusPromo";
import { Skeleton } from "../../components/Skeleton";
import { SubHeader } from "../../components/SubHeader";
import { useAppState } from "../../contexts/App";
import { getPreviousPath } from "../../hooks/PreviousPath";
import CalendarIcon from "../../icons/Calendar";
import ChevronRightIcon from "../../icons/ChevronRight";
import EngineIcon from "../../icons/Engine";
import { getAgenda, knownTimezone, listBookings, listServices } from "../../services/agenda";
import { hasActiveSubscription } from "../../services/api";
import { Container, Section } from "../Settings/styles";
import { Bookings } from "./Bookings";
import { Services } from "./Services";
import { Settings } from "./Settings";
import { MenuCard, MenuList, MenuStatus } from "./styles";
import { todayIn } from "./timezone";

const prefix = "pages.agenda";

/** The agenda's pages, by their address (/agenda/<page>). */
export const AGENDA_PAGES = ["agendamentos", "servicos", "configuracoes"] as const;
export type AgendaPageKey = (typeof AGENDA_PAGES)[number];

const TITLES: Record<AgendaPageKey, string> = {
  agendamentos: "tabs.bookings",
  servicos: "tabs.services",
  configuracoes: "tabs.settings",
};

// A price tag: the services (with their price), in the same strokes as the Keepo's other icons
const ServicesIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M12.59 2.59A2 2 0 0 0 11.17 2H4a2 2 0 0 0-2 2v7.17a2 2 0 0 0 .59 1.42l8.7 8.7a2.43 2.43 0 0 0 3.42 0l6.58-6.58a2.43 2.43 0 0 0 0-3.42z" />
    <circle cx="7.5" cy="7.5" r="1.5" />
  </svg>
);

const useAgendaContext = () => {
  const { appState } = useAppState();
  return {
    siteId: appState.editingSiteId ?? "",
    // without PLUS the agenda is set up all the same (also while the Keepo is being created): PLUS is asked when
    // publishing, and customers only book on a PLUS Keepo
    isPlus: hasActiveSubscription(appState.editingSiteSubscription),
  };
};

/** Without PLUS: the agenda is set up freely; publishing with it asks for PLUS. */
const PlusOffer = ({ siteId }: { siteId: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <PlusPromo compact title={t(`${prefix}.plus`)} onClick={() => navigate(`/subscription/features?site=${siteId}`)} />
  );
};

/**
 * Agenda (PLUS): a menu of its three pages, the bookings, the services and the settings, each with how it is now.
 * The customer books on the Keepo's agenda page.
 */
type Summary = { pending?: number; today?: number; services?: number; enabled?: boolean };

// The last summary of each Keepo: back on the menu it shows at once, then updates
const summaries: Record<string, Summary> = {};

// Where each Keepo's agenda was opened from: back on the menu from one of its pages, the way back stays the same
const openedFromPages: Record<string, boolean> = {};

/**
 * Whether the agenda was opened from the editor's pages (the agenda is one of them): coming in from outside it (to
 * its menu or right to one of its pages), that's where it's opened from now; moving inside it, that stays.
 */
const openedFromEditorPages = (siteId: string) => {
  const previous = getPreviousPath();
  if (!previous?.startsWith("/agenda")) openedFromPages[siteId] = previous === "/editor/pages";
  return !!openedFromPages[siteId];
};

type Tone = "on" | "waiting" | "off";

const AgendaPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { siteId, isPlus } = useAgendaContext();
  const [summary, setSummary] = useState<Summary>(() => summaries[siteId] ?? {});
  // opened from the editor's pages: back to them, otherwise to the Keepo
  const [fromPages] = useState(() => openedFromEditorPages(siteId));

  useEffect(() => {
    if (!siteId) return;
    let current = true;
    const update = (part: Summary) => {
      summaries[siteId] = { ...summaries[siteId], ...part };
      if (current) setSummary(summaries[siteId]);
    };
    // "today" is the agenda's (its timezone comes with it), even on a computer in another timezone
    getAgenda(siteId)
      .then(
        settings => {
          update({ enabled: settings.enabled });
          return settings.timezone;
        },
        () => knownTimezone(siteId)
      )
      .then(timezone => {
        const from = todayIn(timezone);
        const to = new Date(`${from}T12:00:00Z`);
        to.setUTCDate(to.getUTCDate() + 61);
        return listBookings(siteId, from, to.toISOString().slice(0, 10)).then(list =>
          update({
            pending: list.items.filter(b => b.status === "pending").length,
            today: list.items.filter(b => b.date === from && (b.status === "pending" || b.status === "confirmed"))
              .length,
          })
        );
      })
      .catch(() => undefined);
    listServices(siteId)
      .then(items => update({ services: items.length }))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [siteId]);

  // the tabs' old addresses (?aba=) go to their page
  const old = params.get("aba");
  if (old && (AGENDA_PAGES as readonly string[]).includes(old)) {
    return <Navigate to={old} replace />;
  }

  const bookingsStatus = (): [Tone, string] | undefined => {
    if (summary.pending === undefined || summary.today === undefined) return undefined;
    if (summary.pending > 0) return ["waiting", t(`${prefix}.menu.pending`, { count: summary.pending })];
    return summary.today > 0
      ? ["on", t(`${prefix}.menu.today`, { count: summary.today })]
      : ["off", t(`${prefix}.menu.noneToday`)];
  };

  const cards: { key: AgendaPageKey; icon: ReactNode; status?: [Tone, string] }[] = [
    { key: "agendamentos", icon: <CalendarIcon color="currentColor" />, status: bookingsStatus() },
    {
      key: "servicos",
      icon: <ServicesIcon />,
      status:
        summary.services === undefined
          ? undefined
          : summary.services > 0
            ? ["on", t(`${prefix}.menu.services`, { count: summary.services })]
            : ["off", t(`${prefix}.menu.noServices`)],
    },
    {
      key: "configuracoes",
      icon: <EngineIcon color="currentColor" />,
      status:
        summary.enabled === undefined
          ? undefined
          : summary.enabled
            ? ["on", t(`${prefix}.menu.active`)]
            : ["off", t(`${prefix}.menu.inactive`)],
    },
  ];

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.title`)}
        crumbs={fromPages ? [{ label: t("pages.editor.pages.title"), to: "/editor/pages" }] : []}
      />
      {!isPlus && (
        <Section>
          <PlusOffer siteId={siteId} />
        </Section>
      )}
      <Section>
        <MenuList>
          {cards.map(({ key, icon, status }) => (
            <li key={key}>
              <MenuCard type="button" onClick={() => navigate(key)}>
                <span className="icon" aria-hidden="true">
                  {icon}
                </span>
                <span className="text">
                  <span className="title">{t(`${prefix}.${TITLES[key]}`)}</span>
                  <span className="description">{t(`${prefix}.menu.descriptions.${key}`)}</span>
                  {status ? (
                    <MenuStatus $tone={status[0]}>{status[1]}</MenuStatus>
                  ) : (
                    <Skeleton className="status" aria-hidden="true" />
                  )}
                </span>
                <span className="chevron" aria-hidden="true">
                  <ChevronRightIcon width={14} height={14} color="currentColor" />
                </span>
              </MenuCard>
            </li>
          ))}
        </MenuList>
      </Section>
    </Container>
  );
};

/** One page of the agenda: its title, the way back to the agenda, and its content. */
export const AgendaSubPage = ({ page }: { page: AgendaPageKey }) => {
  const { t } = useTranslation();
  const { siteId, isPlus } = useAgendaContext();
  // the settings not saved yet: going back to the agenda asks before dropping them
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // opened right here: where from, for its menu's way back
  useState(() => openedFromEditorPages(siteId));

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.${TITLES[page]}`)}
        crumbs={[{ label: t(`${prefix}.title`), to: "/agenda" }]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      {!isPlus && (
        <Section>
          <PlusOffer siteId={siteId} />
        </Section>
      )}
      <Section>
        {siteId && page === "agendamentos" && <Bookings siteId={siteId} isPlus={isPlus} />}
        {siteId && page === "servicos" && <Services siteId={siteId} />}
        {siteId && page === "configuracoes" && <Settings siteId={siteId} onUnsavedChange={setHasUnsavedChanges} />}
      </Section>
    </Container>
  );
};

export default AgendaPage;
